import React, { FC, useState, useEffect, useMemo } from 'react';
import CustomModal from '../../../utils/components/modal/modal';
import CustomModalHeader from '../../../utils/components/modal/header';
import CustomModalBody from '../../../utils/components/modal/body';
import { useProgrammeExplorerSupporter } from '../ProgrammeExplorerSupporterProvider';

interface Props {
    show: boolean;
    onHide: () => void;
    onGenerate: (selectedMetrics: string[]) => void;
    fileType: 'pdf' | 'ppt';
    isReady?: boolean;
}

const MetricSelectionModal: FC<Props> = ({ show, onHide, onGenerate, fileType, isReady = true }) => {
    const ProgrammeExplorer = useProgrammeExplorerSupporter();

    const getProgrammesClassName = (searchValues: string[]) => {
        const filterProgrammes = ProgrammeExplorer.params.filter_filters.programmes;
        if (filterProgrammes && filterProgrammes.length > 0) {
            const anyProgramMatches = filterProgrammes.some((filterId: number) => {
                const foundProgram = ProgrammeExplorer.programmes.find(program => program.id === filterId);
                return foundProgram && foundProgram.type ? searchValues.includes(foundProgram.type.toLowerCase()) : false;
            });
            return anyProgramMatches ? '' : 'd-none';
        }
        return '';
    };

    const hasProjectCode = (codes: string[]) => {
        const filters: any = ProgrammeExplorer.params.filter_filters;
        const selectedProgrammeIds: number[] = filters.programmes ?? [];
        if (Array.isArray(selectedProgrammeIds) && selectedProgrammeIds.length > 0) {
            const selectedProgrammes = ProgrammeExplorer.programmes
                .filter(p => selectedProgrammeIds.includes(p.id));

            const matchByProgramme = selectedProgrammes.some(p =>
                codes.includes((p.projectCode ?? '').toString())
            );

            if (matchByProgramme) {
                return true;
            }
        }

        if (filters.project_code_media) {
            return codes.includes(filters.project_code_media.toString());
        }

        return false;
    };

    const dynamicMetricsList = useMemo(() => {
        const list = [];
        
        // Impact metrics (Always shown in ProgrammeExplorerBase)
        list.push({ id: 'practice_changing', label: 'Practice Changing', section: 'Impact' });
        list.push({ id: 'valuable', label: 'Valuable', section: 'Impact' });
        list.push({ id: 'knowledge_improving', label: 'Knowledge Improving', section: 'Impact' });
        list.push({ id: 'balanced_and_unbiased', label: 'Balanced and Unbiased', section: 'Impact' });

        // Engagement metrics
        const isSpecialProject = hasProjectCode(['AZ 24-09-01n', 'ME 25-03-03l', 'IP 24-08-02j', 'C2 25-01-05l']);
        
        if (isSpecialProject) {
            list.push({ id: 'engagement_total_v2', label: 'Total Engagement Number', section: 'Engagement' });
        } else {
            list.push({ id: 'engagement_total', label: 'Total Engagement Number', section: 'Engagement' });
        }

        list.push({ id: 'slide_downloads', label: 'Slide Downloads', section: 'Engagement' });

        if (isSpecialProject) {
            list.push({ id: 'video_views_podcast_listens', label: 'Video Views / Podcast Listens', section: 'Engagement' });
        } else {
            list.push({ id: 'video_views', label: 'Video Views', section: 'Engagement' });
            if (getProgrammesClassName(["podcast"]) !== 'd-none') {
                list.push({ id: 'podcast_listens', label: 'Podcast Listens', section: 'Engagement' });
            }
        }

        if (getProgrammesClassName(["micro-learning", "e-learning"]) !== 'd-none') {
            list.push({ id: 'website_visits', label: 'E-learning Visits', section: 'Engagement' });
        }

        // Social Media metrics (Always shown)
        list.push({ id: 'social_media_video_views', label: 'Social Media Video Views', section: 'Social Media' });
        list.push({ id: 'social_media_engagements', label: 'Social Media Engagements', section: 'Social Media' });

        // Include detailed demographics? The backend might expect 'page_views', 'hcps_reached_out' etc., 
        // but the user said "solo las metricas que estan en pantalla para ello hay que seguir la logica de show de las card"
        
        return list;
    }, [ProgrammeExplorer.params.filter_filters, ProgrammeExplorer.programmes]);

    const groupedMetrics = useMemo(() => {
        return dynamicMetricsList.reduce((acc, metric) => {
            if (!acc[metric.section]) {
                acc[metric.section] = [];
            }
            acc[metric.section].push(metric);
            return acc;
        }, {} as Record<string, typeof dynamicMetricsList>);
    }, [dynamicMetricsList]);

    const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);

    useEffect(() => {
        // Reset selected metrics when modal opens or dynamic list changes
        if (show) {
            setSelectedMetrics(dynamicMetricsList.map(m => m.id));
        }
    }, [show, dynamicMetricsList]);

    const handleToggle = (id: string) => {
        if (selectedMetrics.includes(id)) {
            setSelectedMetrics(selectedMetrics.filter(m => m !== id));
        } else {
            setSelectedMetrics([...selectedMetrics, id]);
        }
    };

    const handleSelectAll = () => {
        if (selectedMetrics.length === dynamicMetricsList.length) {
            setSelectedMetrics([]);
        } else {
            setSelectedMetrics(dynamicMetricsList.map(m => m.id));
        }
    };

    return (
        <CustomModal identifier="metric_selection_modal" show={show} onHideModal={onHide} aditionalClasses='d-flex align-items-center'>
            <CustomModalHeader classes="pb-0 border-0">
                <div className="modal-title justify-content-start">
                    <h2>Select Metrics for {fileType.toUpperCase()}</h2>
                </div>
            </CustomModalHeader>
            <CustomModalBody classes="mx-5 mx-xl-18 pt-5 pb-15">
                <div className="mb-5">
                    <div className="form-check form-check-custom form-check-solid mb-5">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            checked={selectedMetrics.length === dynamicMetricsList.length && dynamicMetricsList.length > 0}
                            onChange={handleSelectAll}
                            id="select_all"
                        />
                        <label className="form-check-label fw-bold" htmlFor="select_all">
                            Select All
                        </label>
                    </div>
                    {Object.entries(groupedMetrics).map(([section, metrics]) => (
                        <div key={section} className="mb-6">
                            <h4 className="text-gray-800 fw-bold mb-4">{section}</h4>
                            <div className="row">
                                {metrics.map((metric) => (
                                    <div className="col-md-6 mb-3" key={metric.id}>
                                        <div className="form-check form-check-custom form-check-solid">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={selectedMetrics.includes(metric.id)}
                                                onChange={() => handleToggle(metric.id)}
                                                id={metric.id}
                                            />
                                            <label className="form-check-label" htmlFor={metric.id}>
                                                {metric.label}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="text-center">
                    <button type="button" className="btn btn-light me-3" onClick={onHide}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => onGenerate(selectedMetrics)}
                        disabled={selectedMetrics.length === 0 || !isReady}
                    >
                        Generate {fileType.toUpperCase()}
                    </button>
                </div>
            </CustomModalBody>
        </CustomModal>
    );
};

export default MetricSelectionModal;
